/* Native theme carousels, video playback and details. No page-builder runtime. */
(() => {
  if (window.fnContentInitialized) return;
  window.fnContentInitialized = true;
  const motion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  const cleanup = new WeakMap();
  const interactionPaused = new Set();
  // Photo-review rails hand control to the customer for the rest of this page visit.
  // A section re-render keeps that choice; a full page load starts a new visit.
  function pauseOnInteraction(carousel, track, listen, pause) {
    const stop = () => {
      const key=carousel.closest('.shopify-section')?.id || carousel;
      interactionPaused.add(key);
      carousel.dataset.fnInteractionPaused='true';
      pause();
      // Stop any in-flight automatic smooth scroll before a customer's gesture.
      const behavior=track.style.getPropertyValue('scroll-behavior'),priority=track.style.getPropertyPriority('scroll-behavior');
      const snap=track.style.getPropertyValue('scroll-snap-type'),snapPriority=track.style.getPropertyPriority('scroll-snap-type');
      track.style.setProperty('scroll-behavior','auto','important');track.style.setProperty('scroll-snap-type','none','important');
      track.scrollTo({left:track.scrollLeft,behavior:'instant'});
      if(behavior)track.style.setProperty('scroll-behavior',behavior,priority);else track.style.removeProperty('scroll-behavior');
      if(snap)track.style.setProperty('scroll-snap-type',snap,snapPriority);else track.style.removeProperty('scroll-snap-type');
    };
    for(const event of ['pointerdown','touchstart','wheel'])listen(carousel,event,stop,{capture:true,passive:true});
    for(const event of ['focusin','keydown','click'])listen(carousel,event,stop,{capture:true});
  }
  let videoDialog, videoElement, lastVideoTrigger;
  function stopVideo() {
    if (!videoElement) return;
    videoElement.pause();
    videoElement.removeAttribute('src');
    videoElement.load();
    if (lastVideoTrigger?.isConnected) lastVideoTrigger.focus({preventScroll:true});
  }
  function openVideo(trigger) {
    const src = trigger.dataset.fnVideo;
    if (!src) return;
    if (!videoDialog) {
      videoDialog = document.createElement('dialog');
      videoDialog.className = 'fn-video-dialog';
      videoDialog.setAttribute('aria-label', 'Customer video');
      const close = document.createElement('button');
      close.type = 'button'; close.className = 'fn-video-close';
      close.setAttribute('aria-label','Close video'); close.textContent = '×';
      close.addEventListener('click', () => videoDialog.close());
      videoElement = document.createElement('video');
      videoElement.controls = true; videoElement.playsInline = true;
      videoElement.preload = 'metadata';
      videoDialog.append(close, videoElement); document.body.append(videoDialog);
      videoDialog.addEventListener('close', stopVideo);
      videoDialog.addEventListener('click', (e) => { if (e.target === videoDialog) { const r=videoDialog.getBoundingClientRect(); if(e.clientX<r.left||e.clientX>r.right||e.clientY<r.top||e.clientY>r.bottom) videoDialog.close(); } });
    }
    lastVideoTrigger = trigger;
    videoElement.src = src;
    videoElement.poster = trigger.querySelector('img')?.currentSrc || '';
    videoDialog.setAttribute('aria-label', trigger.getAttribute('aria-label') || 'Customer video');
    videoDialog.showModal();
    videoElement.play().catch(() => {});
  }
  // Opt-in photo-review loop. Originals stay editable; inert visual copies bridge
  // either end so the viewport never animates backwards through the whole rail.
  function setupInfiniteCarousel(carousel) {
    const track=carousel.querySelector('[data-fn-track]');
    if(!track)return;
    const originals=[...track.children];
    const prev=carousel.querySelector('[data-fn-prev]'), next=carousel.querySelector('[data-fn-next]');
    const dots=carousel.querySelector('[data-fn-dots]'), status=carousel.querySelector('[data-fn-status]');
    const autoplay=carousel.dataset.fnAutoplay==='true';
    const stopAfterInteraction=carousel.dataset.fnStopOnInteraction==='true';
    const hideAutoplayControl=carousel.dataset.fnHideAutoplayControl==='true';
    const interval=Math.max(2,Math.min(20,Number(carousel.dataset.fnInterval)||5))*1000;
    const reduced=window.matchMedia('(prefers-reduced-motion: reduce)');
    let controls=carousel.querySelector('.fn-carousel-controls'), pauseButton, madeControls=false;
    let visible=[], positions=[], copies=[], origin=0, circumference=0, active=0;
    let timer, settleTimer, frame, measureFrame, signature='', disposed=false, seamless=false;
    let hovered=false, pointerDown=false, manuallyPaused=stopAfterInteraction && interactionPaused.has(carousel.closest('.shopify-section')?.id || carousel), sectionSelected=false, blockSelected=false, inView=true, moving=false;
    const unbind=[];
    const listen=(node,event,handler,options)=>{node.addEventListener(event,handler,options);unbind.push(()=>node.removeEventListener(event,handler,options));};
    const canAutoplay=()=>autoplay && seamless && !moving && !manuallyPaused && !hovered && !pointerDown && !sectionSelected && !blockSelected && inView && !document.hidden && !reduced.matches && !carousel.contains(document.activeElement);
    const schedule=()=>{
      clearTimeout(timer);
      if(disposed)return;
      const running=canAutoplay();
      if(status)status.setAttribute('aria-live',running?'off':'polite');
      if(pauseButton){
        pauseButton.disabled=reduced.matches;
        const label=reduced.matches?'Autoplay disabled by reduced-motion preference':manuallyPaused?'Resume autoplay':'Pause autoplay';
        pauseButton.setAttribute('aria-label',label);pauseButton.title=label;
        pauseButton.innerHTML=manuallyPaused?'<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="m8 5 11 7-11 7z"/></svg>':'<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M6 4h4v16H6zM14 4h4v16h-4z"/></svg>';
      }
      if(running)timer=setTimeout(()=>go(active+1),interval);
    };
    // CSS scroll-behavior must also be overridden for an imperceptible rebase.
    const jump=left=>{
      const oldBehavior=track.style.getPropertyValue('scroll-behavior'),oldPriority=track.style.getPropertyPriority('scroll-behavior');
      const oldSnap=track.style.getPropertyValue('scroll-snap-type'),oldSnapPriority=track.style.getPropertyPriority('scroll-snap-type');
      track.style.setProperty('scroll-behavior','auto','important');track.style.setProperty('scroll-snap-type','none','important');
      track.scrollTo({left,behavior:'instant'});
      if(oldBehavior)track.style.setProperty('scroll-behavior',oldBehavior,oldPriority);else track.style.removeProperty('scroll-behavior');
      if(oldSnap)track.style.setProperty('scroll-snap-type',oldSnap,oldSnapPriority);else track.style.removeProperty('scroll-snap-type');
    };
    const sync=()=>{
      let distance=Infinity;
      const left=track.scrollLeft;
      positions.forEach((position,i)=>{
        const d=seamless?Math.min(Math.abs(position-left),Math.abs(position+circumference-left),Math.abs(position-circumference-left)):Math.abs(position-left);
        if(d<distance){distance=d;active=i;}
      });
      if(dots)[...dots.children].forEach((dot,i)=>dot.setAttribute('aria-current',String(i===active)));
      if(status)status.textContent=positions.length?`Review ${active+1} of ${positions.length}`:'';
      if(prev)prev.disabled=!seamless;if(next)next.disabled=!seamless;
      if(controls)controls.hidden=!seamless;
    };
    const normalize=()=>{
      if(!seamless || pointerDown)return;
      const left=track.scrollLeft;
      if(left<origin-1)jump(left+circumference);
      else if(left>=origin+circumference-1)jump(left-circumference);
    };
    const settled=()=>{
      clearTimeout(settleTimer);
      if(disposed)return;
      normalize();moving=false;sync();schedule();
    };
    const go=index=>{
      if(!seamless || !positions.length)return;
      const normalized=(index%positions.length+positions.length)%positions.length;
      let left=positions[normalized];
      if(index<0)left-=circumference;
      else if(index>=positions.length)left+=circumference;
      active=normalized;moving=true;clearTimeout(timer);
      track.scrollTo({left,behavior:reduced.matches?'instant':'smooth'});
      sync();clearTimeout(settleTimer);settleTimer=setTimeout(settled,180);
    };
    const createCopy=source=>{
      const copy=source.cloneNode(true), sourceNodes=[source,...source.querySelectorAll('*')], copyNodes=[copy,...copy.querySelectorAll('*')];
      // Scoped editor typography/color selectors cannot be copied with block IDs.
      // Preserve their computed appearance while removing every editor identity.
      const appearance=['font-family','font-size','font-weight','font-style','line-height','letter-spacing','text-align','color','background-color','border-color'];
      copyNodes.forEach((node,i)=>{
        const computed=getComputedStyle(sourceNodes[i]);
        appearance.forEach(property=>node.style.setProperty(property,computed.getPropertyValue(property),'important'));
        [...node.attributes].forEach(attribute=>{if(attribute.name==='id'||attribute.name==='data-block-id'||attribute.name.startsWith('data-shopify')||['for','aria-labelledby','aria-describedby','aria-controls'].includes(attribute.name))node.removeAttribute(attribute.name);});
        if(node.matches('a,button,input,select,textarea,[tabindex]'))node.setAttribute('tabindex','-1');
      });
      copy.dataset.fnClone='true';copy.setAttribute('aria-hidden','true');copy.setAttribute('inert','');
      return copy;
    };
    const renderDots=()=>{
      if(!dots)return;
      dots.replaceChildren();
      visible.forEach((slide,i)=>{const dot=document.createElement('button');dot.type='button';dot.className='fn-carousel-dot';dot.setAttribute('aria-label',`Go to review ${i+1}`);dot.addEventListener('click',()=>go(i));dots.append(dot);});
    };
    const measure=()=>{
      if(disposed)return;
      const shown=originals.filter(slide=>slide.offsetWidth>0);
      const style=getComputedStyle(track),gap=parseFloat(style.columnGap)||0;
      // Only a real size/visibility change rebuilds copies, never a scroll event.
      const nextSignature=[track.clientWidth,gap,...originals.map(s=>`${s.offsetWidth}:${s.offsetHeight}`)].join('|');
      if(signature===nextSignature)return;
      signature=nextSignature;clearTimeout(timer);clearTimeout(settleTimer);
      const current=visible[active];copies.forEach(copy=>copy.remove());copies=[];
      visible=shown;active=Math.max(0,visible.indexOf(current));
      const length=shown.reduce((sum,slide)=>sum+slide.offsetWidth+gap,0);
      seamless=shown.length>1 && length-gap>track.clientWidth+2;
      if(seamless){
        const before=document.createDocumentFragment(),after=document.createDocumentFragment();
        shown.forEach(slide=>{const copy=createCopy(slide);copies.push(copy);before.append(copy);});
        shown.forEach(slide=>{const copy=createCopy(slide);copies.push(copy);after.append(copy);});
        track.prepend(before);track.append(after);
      }
      const base=track.firstElementChild?.offsetLeft||0;
      positions=shown.map(slide=>slide.offsetLeft-base);
      origin=positions[0]||0;
      circumference=seamless?(copies[shown.length].offsetLeft-shown[0].offsetLeft):0;
      renderDots();jump(positions[active]||0);moving=false;sync();schedule();
    };
    const onScroll=()=>{
      if(disposed)return;
      moving=true;clearTimeout(timer);cancelAnimationFrame(frame);frame=requestAnimationFrame(sync);
      clearTimeout(settleTimer);settleTimer=setTimeout(settled,150);
    };
    listen(track,'scroll',onScroll,{passive:true});listen(track,'scrollend',settled);
    if(prev)listen(prev,'click',()=>go(active-1));if(next)listen(next,'click',()=>go(active+1));
    listen(carousel,'pointerdown',()=>{pointerDown=true;schedule();});
    const release=()=>{pointerDown=false;clearTimeout(settleTimer);settleTimer=setTimeout(settled,150);};
    listen(window,'pointerup',release);listen(window,'pointercancel',release);
    if(autoplay){
      if(!hideAutoplayControl){
        if(!controls){controls=document.createElement('div');controls.className='fn-carousel-controls';carousel.append(controls);madeControls=true;}
        pauseButton=document.createElement('button');pauseButton.type='button';pauseButton.className='fn-carousel-arrow fn-carousel-toggle';controls.append(pauseButton);
        listen(pauseButton,'click',()=>{manuallyPaused=!manuallyPaused;schedule();});
      }
      if(stopAfterInteraction)pauseOnInteraction(carousel,track,listen,()=>{manuallyPaused=true;schedule();});
      listen(carousel,'pointerenter',e=>{if(e.pointerType!=='touch'){hovered=true;schedule();}});
      listen(carousel,'pointerleave',()=>{hovered=false;schedule();});
      listen(carousel,'focusin',schedule);listen(carousel,'focusout',()=>queueMicrotask(schedule));
      listen(document,'visibilitychange',schedule);listen(reduced,'change',schedule);
    }
    const section=carousel.closest('.shopify-section');
    const belongs=e=>e.target===section || e.target.contains(carousel);
    listen(document,'shopify:section:select',e=>{if(belongs(e)){sectionSelected=true;schedule();}});
    listen(document,'shopify:section:deselect',e=>{if(belongs(e)){sectionSelected=false;schedule();}});
    listen(document,'shopify:block:select',e=>{if(carousel.contains(e.target)){blockSelected=true;const index=visible.indexOf(e.target.closest('.fn-carousel-slide'));if(index>=0){active=index;jump(positions[index]);sync();}schedule();}});
    listen(document,'shopify:block:deselect',e=>{if(carousel.contains(e.target)){blockSelected=false;schedule();}});
    const observer=new ResizeObserver(()=>{cancelAnimationFrame(measureFrame);measureFrame=requestAnimationFrame(measure);});observer.observe(track);originals.forEach(slide=>observer.observe(slide));
    const visibility=typeof IntersectionObserver==='function'?new IntersectionObserver(entries=>{inView=entries.some(entry=>entry.isIntersecting);schedule();},{threshold:0}):null;
    visibility?.observe(carousel);
    cleanup.set(carousel,()=>{
      disposed=true;clearTimeout(timer);clearTimeout(settleTimer);cancelAnimationFrame(frame);cancelAnimationFrame(measureFrame);observer.disconnect();visibility?.disconnect();unbind.forEach(fn=>fn());copies.forEach(copy=>copy.remove());pauseButton?.remove();if(madeControls)controls.remove();cleanup.delete(carousel);
    });
    measure();
  }
  function setupCarousel(carousel) {
    if (cleanup.has(carousel)) return;
    if (carousel.dataset.fnInfinite === 'true' && carousel.dataset.fnLoop !== 'false') { setupInfiniteCarousel(carousel); return; }
    const track = carousel.querySelector('[data-fn-track]');
    if (!track) return;
    const slides = [...track.children];
    const prev = carousel.querySelector('[data-fn-prev]'), next = carousel.querySelector('[data-fn-next]');
    const dots = carousel.querySelector('[data-fn-dots]');
    const status = carousel.querySelector('[data-fn-status]');
    const autoplay = carousel.dataset.fnAutoplay === 'true';
    const stopAfterInteraction = carousel.dataset.fnStopOnInteraction === 'true';
    const hideAutoplayControl = carousel.dataset.fnHideAutoplayControl === 'true';
    const loop = carousel.dataset.fnLoop !== 'false';
    const interval = Math.max(2,Math.min(20,Number(carousel.dataset.fnInterval)||5))*1000;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let active = 0, frame, positions = [], timer, pauseButton;
    let hovered = false, pointerDown = false, manuallyPaused = stopAfterInteraction && interactionPaused.has(carousel.closest('.shopify-section')?.id || carousel);
    let sectionSelected = false, blockSelected = false;
    let controls = carousel.querySelector('.fn-carousel-controls');
    const unbind = [];
    const listen = (target,event,handler,options) => {
      target.addEventListener(event,handler,options);
      unbind.push(()=>target.removeEventListener(event,handler,options));
    };
    const canAutoplay = () => autoplay && !manuallyPaused && !hovered && !pointerDown && !document.hidden && !reducedMotion.matches && !sectionSelected && !blockSelected && !carousel.contains(document.activeElement) && positions.length>1 && (loop || active<positions.length-1);
    const schedule = () => {
      clearTimeout(timer);
      const running=canAutoplay();
      if(status)status.setAttribute('aria-live',running?'off':'polite');
      if(pauseButton){
        pauseButton.disabled=reducedMotion.matches;
        const label=reducedMotion.matches?'Autoplay disabled by reduced-motion preference':manuallyPaused?'Resume autoplay':'Pause autoplay';
        pauseButton.setAttribute('aria-label',label);pauseButton.title=label;
        pauseButton.innerHTML=manuallyPaused?'<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="m8 5 11 7-11 7z"/></svg>':'<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M6 4h4v16H6zM14 4h4v16h-4z"/></svg>';
      }
      if(running)timer=setTimeout(()=>{go(active+1);schedule();},interval);
    };
    const go = i => {
      if(!positions.length)return;
      const wrapped=loop && (i<0 || i>=positions.length);
      const index=loop?(i%positions.length+positions.length)%positions.length:Math.max(0,Math.min(positions.length-1,i));
      const position=positions[index];
      if (position === undefined) return;
      active=index;
      track.scrollTo({left:position, behavior:wrapped?'auto':motion()});
      schedule();
    };
    const renderDots = () => {
      if (!dots) return;
      dots.replaceChildren();
      positions.forEach((position,i) => {
        const button=document.createElement('button');
        button.type='button'; button.className='fn-carousel-dot';
        button.setAttribute('aria-label','Go to carousel position '+(i+1));
        button.addEventListener('click',()=>go(i)); dots.append(button);
      });
    };
    const update = () => {
      const left=track.scrollLeft;
      const maximum=Math.max(0,track.scrollWidth-track.clientWidth);
      const visible=slides.filter(slide=>slide.offsetWidth>0);
      // Comparison cards can use independent desktop/mobile CSS order controls.
      // Opt in so their dots follow the visible order without changing other rails.
      if(carousel.dataset.fnVisualOrder==='true')visible.sort((a,b)=>a.offsetLeft-b.offsetLeft);
      const nextPositions=[];
      visible.forEach(slide=>{
        const position=Math.min(maximum, Math.max(0,slide.offsetLeft-visible[0].offsetLeft));
        if(!nextPositions.length || Math.abs(nextPositions[nextPositions.length-1]-position)>2) nextPositions.push(position);
      });
      const changed=nextPositions.length!==positions.length;
      positions=nextPositions;
      if(changed)renderDots();
      let distance=Infinity;
      positions.forEach((position,i)=>{const d=Math.abs(position-left);if(d<distance){distance=d;active=i;}});
      if(prev)prev.disabled=maximum<3 || (!loop && left<=2);
      if(next)next.disabled=maximum<3 || (!loop && left>=maximum-2);
      if(dots)[...dots.children].forEach((d,i)=>d.setAttribute('aria-current',String(i===active)));
      if(status)status.textContent=positions.length ? `Position ${active+1} of ${positions.length}` : '';
      if(controls)controls.hidden=maximum<3;
      schedule();
    };
    const onScroll=()=>{cancelAnimationFrame(frame);frame=requestAnimationFrame(update);};
    const previous=()=>go(active-1), following=()=>go(active+1);
    prev?.addEventListener('click',previous); next?.addEventListener('click',following);
    track.addEventListener('scroll',onScroll,{passive:true});
    if(autoplay){
      if(!hideAutoplayControl){
        if(!controls){controls=document.createElement('div');controls.className='fn-carousel-controls';carousel.append(controls);}
        pauseButton=document.createElement('button');pauseButton.type='button';pauseButton.className='fn-carousel-arrow fn-carousel-toggle';
        controls.append(pauseButton);
        listen(pauseButton,'click',()=>{manuallyPaused=!manuallyPaused;schedule();});
      }
      if(stopAfterInteraction)pauseOnInteraction(carousel,track,listen,()=>{manuallyPaused=true;schedule();});
      listen(carousel,'pointerenter',e=>{if(e.pointerType!=='touch'){hovered=true;schedule();}});
      listen(carousel,'pointerleave',()=>{hovered=false;pointerDown=false;schedule();});
      listen(carousel,'pointerdown',()=>{pointerDown=true;schedule();});
      listen(window,'pointerup',()=>{pointerDown=false;schedule();});
      listen(window,'pointercancel',()=>{pointerDown=false;schedule();});
      listen(carousel,'focusin',schedule);
      listen(carousel,'focusout',()=>queueMicrotask(schedule));
      listen(document,'visibilitychange',schedule);
      listen(reducedMotion,'change',schedule);
      const section=carousel.closest('.shopify-section');
      listen(document,'shopify:section:select',e=>{if(e.target===section || e.target.contains(carousel)){sectionSelected=true;schedule();}});
      listen(document,'shopify:section:deselect',e=>{if(e.target===section || e.target.contains(carousel)){sectionSelected=false;schedule();}});
      listen(document,'shopify:block:select',e=>{if(carousel.contains(e.target)){blockSelected=true;schedule();}});
      listen(document,'shopify:block:deselect',e=>{if(carousel.contains(e.target)){blockSelected=false;schedule();}});
    }
    const observer=new ResizeObserver(update);observer.observe(track);slides.forEach(s=>observer.observe(s));
    update();
    cleanup.set(carousel,()=>{observer.disconnect();cancelAnimationFrame(frame);clearTimeout(timer);unbind.forEach(fn=>fn());pauseButton?.remove();track.removeEventListener('scroll',onScroll);prev?.removeEventListener('click',previous);next?.removeEventListener('click',following);cleanup.delete(carousel);});
  }
  function init(root=document) {
    root.querySelectorAll('[data-fn-carousel]').forEach(setupCarousel);
    root.querySelectorAll('[data-fn-details-group]').forEach(group=>{
      if(group.dataset.fnReady)return;
      group.dataset.fnReady='true';
      if(group.dataset.fnCollapseMobile==='true' && window.matchMedia('(max-width:767px)').matches) group.querySelectorAll(':scope > details').forEach(d=>d.open=false);
      if(group.dataset.fnMultiple==='false') group.addEventListener('toggle', e=>{
        if(e.target.tagName!=='DETAILS'||!e.target.open)return;
        group.querySelectorAll(':scope > details').forEach(d=>{if(d!==e.target)d.open=false;});
      },true);
    });
  }
  document.addEventListener('click',e=>{
    if(e.target.closest('.fn-research-static summary')) {e.preventDefault();return;}
    const trigger=e.target.closest('[data-fn-video]');
    if(trigger && !trigger.disabled){e.preventDefault();openVideo(trigger);}
  });
  document.addEventListener('shopify:section:load',e=>init(e.target));
  document.addEventListener('shopify:section:unload',e=>{e.target.querySelectorAll('[data-fn-carousel]').forEach(c=>cleanup.get(c)?.());if(lastVideoTrigger && e.target.contains(lastVideoTrigger))videoDialog?.close();});
  document.addEventListener('shopify:block:select',e=>{
    const block=e.target.closest('.fn-carousel-slide');
    if(block)block.scrollIntoView({behavior:'auto',block:'nearest',inline:'nearest'});
    const details=e.target.closest('details');if(details)details.open=true;
  });
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init());else init();
})();
